import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Modal, Typography } from '@mui/material';
import useCompaniesGrid from '@/shared/hooks/useCompaniesGrid';
import useInfinityScroll from '@/shared/hooks/useInfinityScroll';
import TableVirtualized from '@/shared/UI/components/TableVirtualized';
import { COLUMNS } from '@/widgets/Company/config/configTable';
import Counter from '@/features/TableControls/Counter';
import CompaniesFilters from './Filters';
import Loader from '@/shared/UI/elements/Loader';
import { PrimaryButton } from '@/shared/UI/styled/global';
import CompanyCreate from './CompanyCreate';

import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

interface CompanySelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelectCompany: (companyId: number) => void;
  currentCompanyId: number|null;
}

const CompanySelectionModal = ({
  open,
  onClose,
  onSelectCompany,
  currentCompanyId
}: CompanySelectionModalProps) => {
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [showCreateCompany, setShowCreateCompany] = useState<boolean>(false);
  const tableContainerRef = useRef();

  const {
    data,
    dataTotalCount,
    isLoading,
    getCompaniesDataGrid,
    filters,
    dataCount,
  } = useCompaniesGrid('companies');

  const onInfinityScroll = async () => {
    await getCompaniesDataGrid();
  };

  const { fetchMoreOnBottomReached } = useInfinityScroll(
    tableContainerRef,
    onInfinityScroll,
    isLoading,
    dataCount,
    dataTotalCount,
  );

  useEffect(() => {
    getCompaniesDataGrid();
  }, []);

  const toggleExpand = (companyId: number) => {
    setExpandedRows(prev => ({
      ...prev,
      [companyId]: !prev[companyId],
    }));
  };

  const buildTree = (flatList: any) => {
    const map: any = new Map();
    const roots: any = [];

    flatList.forEach((item: any) => {
      item.parent = null;
      map.set(item.id, item);
    });

    flatList.forEach((item: any) => {
      if (item.parent) {
        const parent = map.get(item.parent);
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(item);
        } else {
          console.warn(`no parent for company with id: ${item.id}`);
        }
      } else {
        roots.push(item);
      }
    });

    return roots;
  };

  const flattenTree = (nodes: any[], level = 0): any[] => {
    const flat: any[] = [];

    for (const node of nodes) {
      const isExpanded = expandedRows[node.id] || false;
      flat.push({ ...node, level, isExpanded });

      if (isExpanded && node.children?.length > 0) {
        flat.push(...flattenTree(node.children, level + 1));
      }
    }

    return flat;
  };

  const handleSelect = (companyId: number) => {
    onSelectCompany(companyId);
    onClose();
  };

  const processedData = useMemo(() => {
    if (data.length === 0) return [];

    if (Object.keys(filters).length > 0) {
      return data;
    }

    const treeData = buildTree(data);
    return flattenTree(treeData);
  }, [data, expandedRows]);

  useEffect(() => {
    if (Object.keys(filters).length > 0) {
      return;
    }

    const expandAll = (nodes: any[], acc: any = {}) => {
      for (const node of nodes) {
        acc[node.id] = true;
        if (node.children?.length > 0) {
          expandAll(node.children, acc);
        }
      }
      return acc;
    };

    const tree = buildTree(data);
    const allExpanded = expandAll(tree);
    setExpandedRows(allExpanded);
  }, [data]);

  const handleRowClick = (id: any) => {
    handleSelect(id);
  };

  const columns = useMemo(() => {
    return COLUMNS({
      toggleExpand,
      onRowClick: handleRowClick,
    });
  }, [processedData, expandedRows]);

  const createCompany = () => {
    setShowCreateCompany(true);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div
        style={{
          padding: 20,
          background: 'white',
          width: '80%',
          margin: 'auto',
          height: '80%',
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 9999,
          outline: 'none',
          borderRadius: '4px',
          overflowY: 'scroll',
        }}
      >
        {!showCreateCompany && (
          <div>
            <Typography variant="h3" sx={{ marginBottom: '20px' }}>
              Companies
            </Typography>
            <IconButton
              disabled={currentCompanyId == null}
              onClick={handleClose}
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box
              sx={{
                display: 'flex',
                marginBottom: '20px',
                justifyContent: 'space-between',
              }}
            >
              <CompaniesFilters />
              <Box sx={{ alignContent: 'end' }}>
                <PrimaryButton
                  style={{
                    height: '43px',
                  }}
                  onClick={createCompany}
                >
                  <span style={{ fontSize: '15px' }}>Create company</span>
                </PrimaryButton>
              </Box>
            </Box>
            <Box>
              <Box
                className="table-holder"
                style={{ position: 'relative' }}
                onScroll={e =>
                  fetchMoreOnBottomReached(e.target as HTMLDivElement)
                }
                ref={tableContainerRef}
              >
                <Loader show={isLoading} height={'100%'} />
                <TableVirtualized
                  accessRef={tableContainerRef}
                  columns={columns}
                  data={processedData}
                  onScroll={fetchMoreOnBottomReached}
                  rowStyle={{ cursor: 'pointer' }}
                  hasFilters={false}
                />
              </Box>
              <Counter currentCount={dataCount} totalCount={dataTotalCount} />
            </Box>
          </div>
        )}
        {showCreateCompany && (
          <div style={{ padding: '10px 20px' }}>
            {' '}
            <CompanyCreate
              companiesTree={data}
              onClose={() => {
                getCompaniesDataGrid();
                setShowCreateCompany(false);
              }}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CompanySelectionModal;
